import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { X, ChevronDown, ChevronUp } from "lucide-react";

const BASE_URL =
  "https://mvdebook.blr1.digitaloceanspaces.com/";

/* -------------------------------------------------------
   URL REWRITER
------------------------------------------------------- */
const rewriteRelativeUrls = (html, baseUrl) => {
  if (!html) return "";
  if (!baseUrl.endsWith("/")) baseUrl += "/";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const tagsWithUrls = tempDiv.querySelectorAll("[href], [src]");

  tagsWithUrls.forEach((el) => {
    const isLink = el.tagName.toLowerCase() === "a";
    const attr = el.hasAttribute("href") ? "href" : "src";
    const url = el.getAttribute(attr);

    if (
      url &&
      !url.startsWith("http") &&
      !url.startsWith("//") &&
      !url.startsWith("#")
    ) {
      el.setAttribute(attr, baseUrl + url.replace(/^\/+/, ""));
    }

    if (isLink && attr === "href") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
      el.style.color = "#1e40af";
      el.style.textDecoration = "underline";
    }
  });

  return tempDiv.innerHTML;
};

/* -------------------------------------------------------
   ORDER-SAFE ACCORDION PARSER (CRITICAL FIX)
------------------------------------------------------- */
const parseAccordion = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || "", "text/html");

  const result = [];

  // 🔍 Detect real (leaf) panel only
  const isRealPanel = (panel) => {
    const hasHeading =
      panel.querySelector(".panel-heading a") ||
      panel.querySelector(".panel-title") ||
      panel.querySelector(".panel-heading strong");

    const hasNestedPanel = panel.querySelector(
      ":scope .panel.panel-default"
    );

    return hasHeading && !hasNestedPanel;
  };

  // ✅ WALK DOM IN ORIGINAL ORDER (CRITICAL)
  Array.from(doc.body.childNodes).forEach((node, idx) => {
    // --------------------------------------------------
    // ELEMENT NODE
    // --------------------------------------------------
    if (node.nodeType === 1) {
      const el = node;

      // ✅ Detect BOTH legacy + new accordion wrappers
      if (
        el.matches(".panel-group") ||
        el.matches(".ck-accordion-wrapper")
      ) {
        const panels = Array.from(
          el.querySelectorAll(".panel.panel-default")
        ).filter(isRealPanel);

        const mappedPanels = panels.map((panel, index) => {
          const headingEl =
            panel.querySelector(".panel-heading a") ||
            panel.querySelector(".panel-title") ||
            panel.querySelector(".panel-heading");

          const bodyEl = panel.querySelector(
            ".panel-collapse .panel-body"
          );

          return {
            // ⭐⭐⭐ STABLE ID — fixes multi-open bug
            id: `accordion-${idx}-panel-${index}`,

            heading:
              headingEl?.innerHTML || `Section ${index + 1}`,

            body: bodyEl?.innerHTML || "",
          };
        });

        result.push({
          type: "accordion",
          panels: mappedPanels,
        });

        return;
      }

      // --------------------------------------------------
      // NORMAL HTML BLOCK
      // --------------------------------------------------
      result.push({
        type: "html",
        content: el.outerHTML,
      });

      return;
    }

    // --------------------------------------------------
    // TEXT NODE (preserve spacing/content)
    // --------------------------------------------------
    if (node.nodeType === 3 && node.textContent.trim()) {
      result.push({
        type: "html",
        content: node.textContent,
      });
    }
  });

  return result;
};


/* -------------------------------------------------------
   CITATION PROCESSOR
------------------------------------------------------- */

const processCitationsForDisplay = (html, nodeId) => {
  const citationRegex = /~@\[([^\]]+)\]~@([^~]*)~@/g;
  let citationCounter = 0;
  const citations = {};

  // First handle ~@[title]~@[body]~@ format
  let processedHtml = html.replace(citationRegex, (match, title, content) => {
    const citationId = `citation-${nodeId}-${citationCounter++}`;
    citations[citationId] = { title, content };

    return `<span 
      class="citation-link" 
      data-citation-id="${citationId}"
      style="
        background-color: #e3f2fd; 
        color: #1976d2; 
        padding: 2px 6px; 
        border-radius: 4px; 
        cursor: pointer; 
        border: 1px solid #bbdefb;
        font-weight: 500;
        text-decoration: underline;
        transition: all 0.2s ease;
        line-height: 1.55;
      "
      onmouseover="this.style.backgroundColor='#bbdefb'"
      onmouseout="this.style.backgroundColor='#e3f2fd'"
    >[${title}]</span>`;
  });

  // Now handle <a data-content="..." title="...">[text]</a> format
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedHtml;

  const links = tempDiv.querySelectorAll('a[data-content][title]');

  links.forEach((link) => {
    const title = link.getAttribute('title');
    const content = link.getAttribute('data-content');
    const displayText = link.textContent || '[citation]';

    const citationId = `citation-${nodeId}-${citationCounter++}`;
    citations[citationId] = {
      title: title || 'Citation',
      content: content || '',
    };

    const span = document.createElement('span');
    span.className = 'citation-link';
    span.setAttribute('data-citation-id', citationId);
    span.style.cssText = `
      background-color: #e3f2fd;
      color: #1976d2;
      padding: 2px 6px;
      border-radius: 4px;
      cursor: pointer;
      border: 1px solid #bbdefb;
      font-weight: 500;
      text-decoration: underline;
      transition: all 0.2s ease;
      line-height: 1.55;
    `;
    span.setAttribute('onmouseover', "this.style.backgroundColor='#bbdefb'");
    span.setAttribute('onmouseout', "this.style.backgroundColor='#e3f2fd'");
    span.innerHTML = displayText;

    link.replaceWith(span);
  });

  if (!window.citationData) {
    window.citationData = {};
  }
  window.citationData[nodeId] = citations;

  return tempDiv.innerHTML;
};

const stripHtml = (htmlString) => {
  const temp = document.createElement('div');
  temp.innerHTML = htmlString;
  return temp.textContent || temp.innerText || '';
};


/* -------------------------------------------------------
   COMPONENT
------------------------------------------------------- */
const CitationContent = ({ node }) => {
  const [blocks, setBlocks] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  /* ---------- PROCESS CONTENT ---------- */
  useEffect(() => {
    if (!node?.verified_content) return;

    const processed = processCitationsForDisplay(
      node.verified_content,
      node.id
    );

    const withBaseUrl = rewriteRelativeUrls(
      processed,
      BASE_URL
    );

    const parsedBlocks = parseAccordion(withBaseUrl);
    setBlocks(parsedBlocks);
  }, [node]);

  /* ---------- CITATION CLICK ---------- */
  const handleCitationClick = (event) => {
    if (event.target.classList.contains("citation-link")) {
      const citationId =
        event.target.getAttribute("data-citation-id");

      const citation =
        window.citationData?.[node?.id]?.[citationId];

      if (citation) {
        setSelectedCitation(citation);
        setIsBottomSheetOpen(true);
      }
    }
  };

  const togglePanel = (panelId) => {
    setOpenPanel((prev) => (prev === panelId ? null : panelId));
  };

  const closeBottomSheet = () => {
    setIsBottomSheetOpen(false);
    setTimeout(() => setSelectedCitation(null), 250);
  };

  /* =====================================================
     RENDER
  ===================================================== */
  return (
    <>
      {blocks.map((block, idx) => {
        /* ---------- NORMAL HTML ---------- */
        if (block.type === "html") {
          return (
            <div
              key={idx}
              className="prose max-w-none"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(block.content, {
                  ADD_ATTR: [
                    "target",
                    "rel",
                    "data-citation-id",
                  ],
                }),
              }}
              onClick={handleCitationClick}
            />
          );
        }

        /* ---------- ACCORDION ---------- */
        if (block.type === "accordion") {
          return (
            <div key={idx} className="mt-4">
              {block.panels.map((panel) => (
                <div
                  key={panel.id}
                  className="border border-gray-200 rounded-lg mb-2"
                >
                  <button
                    className="w-full flex justify-between items-center p-4 text-left bg-gray-50 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePanel(panel.id);
                    }}
                  >
                    <span
                      className="text-sm font-medium text-gray-800"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          panel.heading
                        ),
                      }}
                    />
                    {openPanel === panel.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>

                  {openPanel === panel.id && (
                    <div className="p-4 bg-white">
                      <div
                        className="prose max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(panel.body),
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        }

        return null;
      })}

      {/* ---------- CITATION BOTTOM SHEET ---------- */}
      {/* {isBottomSheetOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-end"
          onClick={closeBottomSheet}
        >
          <div
            className="bg-white w-1/4 rounded-t-lg max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between p-3 border-b">
              <h3 className="font-semibold">
                Citation Details
              </h3>
              <button onClick={closeBottomSheet}>
                <X size={16} />
              </button>
            </div>

            {selectedCitation && (
              <div className="p-4 overflow-y-auto">
                <h4 className="font-bold mb-2">
                  {selectedCitation.title}
                </h4>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      rewriteRelativeUrls(
                        selectedCitation.content || "",
                        BASE_URL
                      ),
                      { ADD_ATTR: ["target", "rel"] }
                    ),
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )} */}

        {isBottomSheetOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-end"
          onClick={closeBottomSheet}
        >
          <div 
            className={`bg-white w-1/4 rounded-t-lg transform transition-transform duration-300 ease-out max-h-[80vh] flex flex-col  border border-gray-100 ${
              isBottomSheetOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-1 bg-gray-300 rounded-full mx-auto"></div>
              </div>
              <h3 className="text-base font-semibold text-gray-800 flex-1 text-center">
                Citation Details
              </h3>
              <button
                onClick={closeBottomSheet}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            {selectedCitation && (
              <div className="flex-1 overflow-y-auto p-4">
                <div className="mb-3">
                  <h4 className="text-md font-bold text-gray-900 mb-1">
  {stripHtml(selectedCitation.title)}
</h4>
                  <div className="h-0.5 w-10 bg-yellow-500 rounded"></div>
                </div>
                
                <div className="prose max-w-none">
                  <div 
                    className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm"
                  dangerouslySetInnerHTML={{ 
    __html: DOMPurify.sanitize(
      rewriteRelativeUrls(selectedCitation.content || '', 'https://mvdebook.blr1.digitaloceanspaces.com'),
      { ADD_ATTR: ['target', 'rel'] } // 👈 allow these attributes
    )
  }}
                  />
                </div>
              </div>
            )}

            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={closeBottomSheet}
                className="w-full bg-black text-white py-2 rounded-sm font-medium hover:bg-yellow-700 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CitationContent;